import { useEffect, useRef, useState } from 'react';
import type { LayoutTemplateSlot } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Renders a template PNG with overlaid slot rectangles. Click empty space
 * to draw a new rectangle, click a rectangle to select it, drag the body
 * to move, drag the corner handles to resize, click the trash to delete.
 *
 * Coordinates passed/received are 0-1 floats relative to the cover face.
 */

interface SlotEditorProps {
  imageUrl: string;
  /** Optional flat colour painted behind the PNG so transparent areas don't
   *  look broken. Pass the page background colour or a checkered pattern. */
  backgroundColor?: string;
  slots: LayoutTemplateSlot[];
  onChange: (slots: LayoutTemplateSlot[]) => void;
  /** CSS aspect-ratio of the canvas (e.g. '3 / 2' for a coupon). Default: the
   *  210×297 page. */
  aspectRatio?: string;
  /** Text shown in each rectangle's corner instead of its number (coupons:
   *  « Code client », « QR code »…). */
  labels?: string[];
}

type DragState =
  | { type: 'none' }
  | { type: 'creating'; start: { x: number; y: number }; current: { x: number; y: number } }
  | { type: 'moving'; index: number; offset: { x: number; y: number } }
  | { type: 'resizing'; index: number; corner: 'tl' | 'tr' | 'bl' | 'br' };

export function SlotEditor({ imageUrl, backgroundColor, slots, onChange, aspectRatio, labels }: SlotEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState>({ type: 'none' });
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  function getRelCoords(e: React.MouseEvent | MouseEvent) {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  }

  function handleMouseDown(e: React.MouseEvent) {
    // Only start a new draw when the click is on the container itself
    // (not on a slot overlay).
    if (e.target !== containerRef.current) return;
    e.preventDefault();
    const start = getRelCoords(e);
    setDrag({ type: 'creating', start, current: start });
    setSelectedIndex(null);
  }

  // Window-level mouse-move + mouse-up listeners survive the cursor
  // leaving the container during a drag. Re-bound whenever `drag` changes
  // so each gesture has a fresh closure over the current slot index.
  useEffect(() => {
    if (drag.type === 'none') return;

    function onMove(e: MouseEvent) {
      const cur = getRelCoords(e);
      if (drag.type === 'creating') {
        setDrag({ ...drag, current: cur });
      } else if (drag.type === 'moving') {
        const s = slots[drag.index];
        if (!s) return;
        const next = [...slots];
        next[drag.index] = {
          ...s,
          x: Math.max(0, Math.min(1 - s.width, cur.x - drag.offset.x)),
          y: Math.max(0, Math.min(1 - s.height, cur.y - drag.offset.y)),
        };
        onChange(next);
      } else if (drag.type === 'resizing') {
        const s = slots[drag.index];
        if (!s) return;
        let { x, y, width, height } = s;
        if (drag.corner === 'br') {
          width = Math.max(0.02, cur.x - x);
          height = Math.max(0.02, cur.y - y);
        } else if (drag.corner === 'tl') {
          const newX = Math.min(x + width - 0.02, cur.x);
          const newY = Math.min(y + height - 0.02, cur.y);
          width = width + (x - newX);
          height = height + (y - newY);
          x = newX;
          y = newY;
        } else if (drag.corner === 'tr') {
          width = Math.max(0.02, cur.x - x);
          const newY = Math.min(y + height - 0.02, cur.y);
          height = height + (y - newY);
          y = newY;
        } else {
          const newX = Math.min(x + width - 0.02, cur.x);
          width = width + (x - newX);
          x = newX;
          height = Math.max(0.02, cur.y - y);
        }
        const next = [...slots];
        next[drag.index] = { ...s, x, y, width, height };
        onChange(next);
      }
    }

    function onUp() {
      if (drag.type === 'creating') {
        const { start, current } = drag;
        const x = Math.min(start.x, current.x);
        const y = Math.min(start.y, current.y);
        const width = Math.abs(current.x - start.x);
        const height = Math.abs(current.y - start.y);
        if (width > 0.02 && height > 0.02) {
          const next: LayoutTemplateSlot = { x, y, width, height };
          onChange([...slots, next]);
          setSelectedIndex(slots.length);
        }
      }
      setDrag({ type: 'none' });
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, slots]);

  function deleteSlot(i: number) {
    onChange(slots.filter((_, idx) => idx !== i));
    setSelectedIndex(null);
  }

  const previewRect =
    drag.type === 'creating'
      ? {
          left: Math.min(drag.start.x, drag.current.x) * 100,
          top: Math.min(drag.start.y, drag.current.y) * 100,
          width: Math.abs(drag.current.x - drag.start.x) * 100,
          height: Math.abs(drag.current.y - drag.start.y) * 100,
        }
      : null;

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      className={cn(
        'relative rounded-xl overflow-hidden border border-gray-200 select-none cursor-crosshair',
        !aspectRatio && 'aspect-[210/297]',
      )}
      style={{
        ...(aspectRatio ? { aspectRatio } : {}),
        backgroundColor: backgroundColor ?? '#f3f4f6',
        backgroundImage:
          'repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%)',
        backgroundSize: '20px 20px',
      }}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        />
      )}

      {slots.map((s, i) => (
        <SlotOverlay
          key={i}
          slot={s}
          selected={selectedIndex === i}
          onSelect={(e) => {
            e.stopPropagation();
            setSelectedIndex(i);
          }}
          onStartMove={(offsetX, offsetY) =>
            setDrag({ type: 'moving', index: i, offset: { x: offsetX, y: offsetY } })
          }
          onStartResize={(corner) => setDrag({ type: 'resizing', index: i, corner })}
          onDelete={() => deleteSlot(i)}
          number={labels?.[i] ?? i + 1}
        />
      ))}

      {previewRect && (
        <div
          className="absolute border-2 border-dashed border-brand-500 bg-brand-500/15"
          style={{
            left: `${previewRect.left}%`,
            top: `${previewRect.top}%`,
            width: `${previewRect.width}%`,
            height: `${previewRect.height}%`,
          }}
        />
      )}
    </div>
  );
}

function SlotOverlay({
  slot,
  selected,
  onSelect,
  onStartMove,
  onStartResize,
  onDelete,
  number,
}: {
  slot: LayoutTemplateSlot;
  selected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onStartMove: (offsetX: number, offsetY: number) => void;
  onStartResize: (corner: 'tl' | 'tr' | 'bl' | 'br') => void;
  onDelete: () => void;
  number: number | string;
}) {
  return (
    <div
      className={cn(
        'absolute border-2 cursor-move',
        selected
          ? 'border-brand-500 bg-brand-500/15 ring-2 ring-brand-500 ring-offset-1'
          : 'border-blue-500 bg-blue-500/15 hover:bg-blue-500/25',
      )}
      style={{
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        width: `${slot.width * 100}%`,
        height: `${slot.height * 100}%`,
      }}
      onMouseDown={(e) => {
        onSelect(e);
        e.preventDefault();
        const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
        const offsetX = (e.clientX - rect.left) / rect.width - slot.x;
        const offsetY = (e.clientY - rect.top) / rect.height - slot.y;
        onStartMove(offsetX, offsetY);
      }}
    >
      <span className="absolute top-1 left-1 text-xs font-semibold bg-white/90 text-gray-800 rounded px-1.5">
        {number}
      </span>

      {selected && (
        <>
          <button
            className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm leading-none flex items-center justify-center shadow z-30"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            type="button"
            title="Supprimer"
          >
            ×
          </button>
          {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
            <CornerHandle key={corner} corner={corner} onStart={() => onStartResize(corner)} />
          ))}
        </>
      )}
    </div>
  );
}

function CornerHandle({
  corner,
  onStart,
}: {
  corner: 'tl' | 'tr' | 'bl' | 'br';
  onStart: () => void;
}) {
  const pos = {
    tl: 'top-0 left-0 cursor-nw-resize -translate-x-1/2 -translate-y-1/2',
    tr: 'top-0 right-0 cursor-ne-resize translate-x-1/2 -translate-y-1/2',
    bl: 'bottom-0 left-0 cursor-sw-resize -translate-x-1/2 translate-y-1/2',
    br: 'bottom-0 right-0 cursor-se-resize translate-x-1/2 translate-y-1/2',
  }[corner];
  return (
    <div
      className={cn('absolute w-3.5 h-3.5 bg-white border-2 border-brand-500 rounded-sm z-20', pos)}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onStart();
      }}
    />
  );
}
